import { useState, useCallback, useEffect } from 'react';
import { useWorkspaces } from './useWorkspaces';
import { useToast } from './use-toast';
import { supabase } from '@/lib/supabase';
import type { EmailProvider, SendEmailOptions } from '@/lib/email/types';
import { safeBase64Encode, formatEmailContent } from '@/lib/email/utils';
import { outlookService } from '@/lib/email/outlook';
import { captureError } from '@/lib/sentry';

export function useEmailProvider() {
  const [loading, setLoading] = useState(false);
  const [providers, setProviders] = useState<EmailProvider[]>([]);
  const { currentWorkspace } = useWorkspaces();
  const { toast } = useToast();

  const fetchProviders = useCallback(async () => {
    if (!currentWorkspace) return [];
    try {
      // NOTE: select('*') no longer includes the encrypted token columns
      // because they have type bytea — PostgREST returns them as base64 which
      // is useless to clients. Tokens are fetched on-demand from the
      // email_provider_get_tokens RPC inside the send/refresh paths instead.
      // For the UI we only need provider metadata.
      const { data, error } = await supabase
        .from('email_providers')
        .select('id, type, email, workspace_id, user_id, expires_at, signature, physical_address, display_name, allowed_days, start_time, end_time, min_interval_minutes, provider_settings, timezone, created_at')
        .eq('workspace_id', currentWorkspace.id);
      if (error) throw error;
      setProviders((data || []) as unknown as EmailProvider[]);
    } catch (error) {
      console.error('Error fetching email providers:', error);
      captureError(error as Error, { feature: 'email-providers-fetch' });
      toast({
        title: "Error",
        description: "Failed to load email providers",
        variant: "destructive",
      });
      setProviders([]);
    }
  }, [currentWorkspace?.id, toast]);

  useEffect(() => {
    fetchProviders();
  }, [currentWorkspace?.id, fetchProviders]); // Added fetchProviders to dependency array

  const sendEmail = async (options: SendEmailOptions) => {
    if (!currentWorkspace) {
      throw new Error('No workspace selected');
    }
    setLoading(true);
    try {
      if (!providers.length) {
        throw new Error('No email providers connected');
      }
      const provider = providers.find(p => p.id === options.providerId);
      if (!provider) {
        throw new Error('Selected email provider not found');
      }
      if (provider.type === 'gmail') {
        await sendGmailEmail(provider, options);
      } else if (provider.type === 'outlook') {
        await sendOutlookEmail(provider, options);
      }
      toast({
        description: "Email sent successfully",
      });
    } catch (error) {
      console.error('Error sending email:', error);
      captureError(error as Error, { feature: 'email-send' });
      toast({
        title: "Error",
        description: "Failed to send email",
        variant: "destructive",
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Refresh delegates to the server-side token-refresh edge function which
  // owns the encrypted token storage. Clients cannot read the encrypted
  // refresh_token, so all refresh paths route through the edge fn.
  const refreshGmailToken = async (provider: EmailProvider): Promise<string> => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/token-refresh`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ provider_id: provider.id }),
        },
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Token refresh failed');
      }
      const data = await response.json();
      // Local-state cache is intentionally NOT updated with access_token any
      // more — the EmailProvider type no longer carries it. Callers must call
      // refreshGmailToken() right before send to get a fresh token.
      return data.access_token as string;
    } catch (error: any) {
      console.error('Error refreshing token:', error);
      if (error.message.includes('Token refresh failed') || error.message.includes('invalid_grant')) {
        // Force user to reconnect their account
        await supabase
          .from('email_providers')
          .delete()
          .eq('id', provider.id);

        setProviders(prev => prev.filter(p => p.id !== provider.id));
      }

      throw new Error('Email account needs to be reconnected. Please disconnect and reconnect your account.');
    }
  };

  const sendGmailEmail = async (provider: EmailProvider, options: SendEmailOptions) => {
    // After encryption, EmailProvider no longer carries access_token. Always
    // fetch via the edge fn (which returns the cached access_token if not
    // expired — see token-refresh/index.ts cache-hit path).
    let accessToken: string;
    try {
      accessToken = await refreshGmailToken(provider);
    } catch (e) {
      throw new Error('No access token available. Please reconnect your email account.');
    }

    try {

      // Create MIME message
      const message = formatEmailContent({
        from: options.from || provider.email,
        to: options.to,
        cc: options.cc,
        bcc: options.bcc,
        subject: options.subject,
        body: options.body,
        signature: options.signature
      });

      const encodedMessage = safeBase64Encode(message)
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');

      const response = await fetch(`https://gmail.googleapis.com/gmail/v1/users/me/messages/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          raw: encodedMessage,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(`Failed to send Gmail: ${error.error?.message || 'Unknown error'}`);
      }

    } catch (error: any) {
      // If token refresh failed or any other error occurred
      if (error.message.includes('Token refresh failed') || error.message.includes('invalid_grant')) {
        await supabase
          .from('email_providers')
          .delete()
          .eq('id', provider.id);
        
        setProviders(prev => prev.filter(p => p.id !== provider.id));
        throw new Error('Email account needs to be reconnected. Please disconnect and reconnect your account.');
      }

      throw error;
    }
  };

  const refreshOutlookToken = async (provider: EmailProvider): Promise<string> => {
    try {
      const { accessToken, expiresOn } = await outlookService.refreshAccessToken();
      // MSAL succeeded. Persist via the token-refresh edge fn override path —
      // clients cannot UPDATE encrypted bytea columns directly.
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      const persistResp = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/token-refresh`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            provider_id: provider.id,
            override_access_token: accessToken,
            override_expires_at: expiresOn ? expiresOn.toISOString() : null,
          }),
        },
      );
      if (!persistResp.ok) {
        console.warn('Failed to persist MSAL access_token; continuing with in-memory token');
      }
      return accessToken;
    } catch {
      console.log('MSAL silent refresh failed, trying server-side refresh...');
    }

    // Server-side fallback using stored refresh_token
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/token-refresh`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ provider_id: provider.id }),
        },
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Token refresh failed' }));
        throw new Error(error.error || 'Token refresh failed');
      }

      const data = await response.json();
      return data.access_token;
    } catch (error) {
      console.error('Server-side token refresh also failed:', error);
      throw new Error('Email account needs to be reconnected. Please disconnect and reconnect your account.');
    }
  };

  const sendOutlookEmail = async (provider: EmailProvider, options: SendEmailOptions) => {
    // EmailProvider no longer carries access_token. Refresh on every send;
    // the MSAL silent-refresh path is fast and cached.
    let accessToken: string;
    try {
      accessToken = await refreshOutlookToken(provider);
    } catch (e) {
      throw new Error('No access token available. Please reconnect your email account.');
    }

    // Preserve blank line spacing for Outlook:
    // 1. Reset paragraph margins to prevent CSS margin collapsing
    // 2. Fill empty paragraphs with &nbsp; so they render with line height
    const outlookBody = options.body
      .replace(/<p>/g, '<p style="margin:0;">')
      .replace(/<p style="margin:0;"><\/p>/g, '<p style="margin:0;">&nbsp;</p>')
      .replace(/<p style="margin:0;"><br\s*\/?><\/p>/g, '<p style="margin:0;">&nbsp;</p>');

    const response = await fetch('https://graph.microsoft.com/v1.0/me/sendMail', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        message: {
          subject: options.subject,
          body: {
            contentType: 'HTML',
            content: outlookBody,
          },
          toRecipients: [
            {
              emailAddress: {
                address: options.to,
              },
            },
          ],
          ...(options.cc?.length ? {
            ccRecipients: options.cc.map(email => ({
              emailAddress: { address: email }
            }))
          } : {}),
          ...(options.bcc?.length ? {
            bccRecipients: options.bcc.map(email => ({
              emailAddress: { address: email }
            }))
          } : {})
        },
      }),
    });

    if (!response.ok) {
      const error = await response.json();

      // On InvalidAuthenticationToken, attempt one refresh + retry
      if (error.error?.code === 'InvalidAuthenticationToken') {
        try {
          accessToken = await refreshOutlookToken(provider);
          const retryResponse = await fetch('https://graph.microsoft.com/v1.0/me/sendMail', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              message: {
                subject: options.subject,
                body: {
                  contentType: 'HTML',
                  content: outlookBody,
                },
                toRecipients: [
                  {
                    emailAddress: {
                      address: options.to,
                    },
                  },
                ],
                ...(options.cc?.length ? {
                  ccRecipients: options.cc.map(email => ({
                    emailAddress: { address: email }
                  }))
                } : {}),
                ...(options.bcc?.length ? {
                  bccRecipients: options.bcc.map(email => ({
                    emailAddress: { address: email }
                  }))
                } : {})
              },
            }),
          });

          if (!retryResponse.ok) {
            const retryError = await retryResponse.json();
            throw new Error(`Failed to send Outlook email: ${retryError.error?.message || 'Unknown error'}`);
          }
          return;
        } catch {
          throw new Error('Email account needs to be reconnected. Please disconnect and reconnect your account.');
        }
      }

      throw new Error(`Failed to send Outlook email: ${error.error?.message || 'Unknown error'}`);
    }
  };

  return {
    loading,
    providers,
    sendEmail,
  };
}